export class OcppError extends Error {
  constructor(message: string) {
    super(message);
    this.name = new.target.name;
  }
}

/**
 * A CALLERROR frame from the CSMS, lifted to a JS exception.
 *
 * `errorCode` is widened to `string` so unknown/vendor-specific codes do not
 * break the contract. Documented codes are exported as `OCPP_ERROR_CODES` for
 * call sites that want to switch on them.
 */
export class OcppCallError extends OcppError {
  constructor(
    public readonly messageId: string,
    public readonly errorCode: string,
    public readonly errorDescription: string,
    public readonly errorDetails: Record<string, unknown> = {},
  ) {
    super(`OCPP CALLERROR [${errorCode}] ${errorDescription}`);
  }
}

/** A CALL went out, no CALLRESULT/CALLERROR arrived in time. */
export class OcppTimeoutError extends OcppError {
  constructor(
    public readonly messageId: string,
    public readonly action: string,
    public readonly timeoutMs: number,
  ) {
    super(`OCPP CALL ${action} (${messageId}) timed out after ${timeoutMs} ms`);
  }
}

/** Ajv said the payload doesn't match the schema. */
export class OcppValidationError extends OcppError {
  constructor(
    public readonly action: string,
    public readonly direction: 'request' | 'response',
    public readonly issues: readonly string[],
  ) {
    super(`OCPP ${action} ${direction} failed schema validation:\n  - ${issues.join('\n  - ')}`);
  }
}

export class OcppFrameParseError extends OcppError {
  constructor(detail: string) {
    super(`OCPP frame parse error: ${detail}`);
  }
}

/**
 * Thrown by an adapter when a method exists on the version-neutral
 * `OcppAdapter` interface but the adapter has not yet implemented the
 * action for its OCPP version (e.g. the v201 adapter while Session 10a
 * only ships Boot / Heartbeat / StatusNotification).
 */
export class OcppNotImplementedError extends OcppError {
  constructor(
    public readonly action: string,
    public readonly version: string,
  ) {
    super(`OCPP ${action} not implemented in ${version} adapter`);
  }
}
